import React, { useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Badge } from './ui/badge';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { useApp } from '../context/AppContext';
import { Prospects, Templates } from '../lib/api';
import EmailHistoryList from './EmailHistoryList';
import { SOURCES, PROSPECT_STATUSES, PROSPECT_LABELS } from '../lib/prospectConfig';
import { Send, Wand2, Trash2, Copy, ExternalLink, Mail, Linkedin, Instagram, Twitter, Info, MessageSquare, Eye } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import EmailPreviewDialog from './EmailPreviewDialog';

export default function ProspectDetailSheet({ prospectId, open, onOpenChange, onChanged }) {
  const { activeShow } = useApp();
  const [p, setP] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState('details');
  const [draft, setDraft] = useState({ templateId: '', subject: '', body: '', includePrEmail: true });
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const saveTimer = useRef(null);
  const pendingPatch = useRef({});

  const load = async () => {
    if (!prospectId) return;
    const data = await Prospects.get(prospectId);
    setP(data);
  };

  useEffect(() => { setTab('details'); if (open) load(); }, [prospectId, open]); // eslint-disable-line
  useEffect(() => {
    Templates.list().then(t => setTemplates(t.filter(x => x.category === 'prospect' || x.category === 'app_user')));
  }, []);

  // Flush pending edits when sheet closes
  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (Object.keys(pendingPatch.current).length && p?.id) {
        Prospects.update(p.id, pendingPatch.current).then(() => onChanged?.()).catch(() => {});
        pendingPatch.current = {};
      }
    };
  // eslint-disable-next-line
  }, [p?.id]);

  if (!p) return null;

  const set = (patch) => {
    // Optimistic local update — instant
    setP(prev => ({ ...prev, ...patch }));
    // Accumulate the patch and debounce the API call
    pendingPatch.current = { ...pendingPatch.current, ...patch };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const toSend = pendingPatch.current;
      pendingPatch.current = {};
      try {
        await Prospects.update(p.id, toSend);
        onChanged?.();
      } catch (e) { toast({ title: 'Save failed' }); }
    }, 600);
  };

  const bookingLink = `${window.location.origin}/book/${p.showId}?prospect=${p.id}`;

  const applyTemplate = (tplId) => {
    const t = templates.find(x => x.id === tplId);
    if (!t) return;
    const first = (p.name || '').split(' ')[0] || 'there';
    const sub = t.subject.replace(/{{showName}}/g, activeShow.name).replace(/{{firstName}}/g, first);
    const body = t.body
      .replace(/{{firstName}}/g, first)
      .replace(/{{showName}}/g, activeShow.name)
      .replace(/{{company}}/g, p.company || '')
      .replace(/{{bookingLink}}/g, bookingLink)
      .replace(/{{referrer}}/g, p.sourceDetail || 'a mutual friend')
      .replace(/{{topic}}/g, p.sourceDetail || 'your work')
      .replace(/{{showFocus}}/g, activeShow.id === 'wine-talks' ? 'wine' : 'American music & culture')
      .replace(/{{hook}}/g, '')
      .replace(/{{specificThought}}/g, '');
    setDraft(d => ({ ...d, templateId: tplId, subject: sub, body }));
  };

  const generateAi = async () => {
    setDrafting(true);
    try {
      const r = await Prospects.aiLetter(p.id, 'intro');
      setDraft(d => ({ ...d, subject: r.subject, body: r.body }));
      toast({ title: 'AI draft ready' });
    } catch (e) { toast({ title: 'AI draft failed', description: e?.response?.data?.detail || String(e) }); }
    finally { setDrafting(false); }
  };

  const send = async () => {
    if (!draft.subject || !draft.body) { toast({ title: 'Subject & body required' }); return; }
    setSending(true);
    try {
      const r = await Prospects.sendLetter(p.id, draft);
      if (r.sent) { toast({ title: 'Letter sent', description: `To ${p.email}${draft.includePrEmail && p.prEmail ? ` + ${p.prEmail}` : ''}` }); await load(); onChanged?.(); }
      else { toast({ title: 'Send failed', description: r.reason || '' }); }
    } catch (e) { toast({ title: 'Send failed' }); } finally { setSending(false); }
  };

  const sourceConfig = SOURCES.find(s => s.value === p.source) || SOURCES[SOURCES.length - 1];
  const statusConfig = PROSPECT_STATUSES.find(s => s.value === p.status) || PROSPECT_STATUSES[0];
  const labels = p.labels?.length ? p.labels : ['podcast_guest'];
  const toggleLabel = (v) => {
    const has = labels.includes(v);
    const next = has ? labels.filter(l => l !== v) : [...labels, v];
    if (!next.length) { toast({ title: 'At least one type required' }); return; }
    set({ labels: next });
  };
  const guestTemplates = templates.filter(t => t.category === 'prospect');
  const appUserTemplates = templates.filter(t => t.category === 'app_user');
  const visibleTemplates = [
    ...(labels.includes('podcast_guest') ? guestTemplates : []),
    ...(labels.includes('app_user') ? appUserTemplates : []),
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[640px] overflow-y-auto p-0">
        <div className="p-5 border-b border-stone-200" style={{ background: `linear-gradient(135deg, ${activeShow.color}, ${activeShow.color}dd)` }}>
          <SheetHeader>
            <SheetTitle className="text-white">
              <div>
                <div className="text-[20px] font-serif">{p.name}</div>
                <div className="text-[12.5px] opacity-85 font-normal">{p.role}{p.role && p.company ? ' • ' : ''}{p.company}</div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }} className="text-[10px] hover:bg-white/20">{sourceConfig.label}</Badge>
                  <Badge style={{ background: statusConfig.color, color: 'white' }} className="text-[10px] hover:opacity-100">{statusConfig.label}</Badge>
                  {labels.map(l => {
                    const cfg = PROSPECT_LABELS.find(x => x.value === l);
                    return cfg ? <Badge key={l} style={{ background: 'rgba(255,255,255,0.25)', color: 'white' }} className="text-[10px] hover:bg-white/25">{cfg.label}</Badge> : null;
                  })}
                  {p.contactCount > 0 && <span className="text-[10.5px] text-white/80">contacted {p.contactCount}×</span>}
                </div>
              </div>
            </SheetTitle>
          </SheetHeader>
        </div>

        <div className="px-5 pt-4 flex gap-2 border-b">
          <button onClick={() => setTab('details')} className={`px-3 py-1.5 text-[13px] ${tab === 'details' ? 'border-b-2 border-stone-900 font-medium' : 'text-stone-500'}`}>Details</button>
          <button onClick={() => setTab('letter')} className={`px-3 py-1.5 text-[13px] ${tab === 'letter' ? 'border-b-2 border-stone-900 font-medium' : 'text-stone-500'}`}>Reach-out letter</button>
          <button onClick={() => setTab('booking')} className={`px-3 py-1.5 text-[13px] ${tab === 'booking' ? 'border-b-2 border-stone-900 font-medium' : 'text-stone-500'}`}>Booking link</button>
          <button onClick={() => setTab('emails')} data-testid="prospect-tab-emails" className={`px-3 py-1.5 text-[13px] ${tab === 'emails' ? 'border-b-2 border-stone-900 font-medium' : 'text-stone-500'}`}>Emails</button>
        </div>

        {tab === 'details' && (
          <div className="p-5 space-y-3">
            <div>
              <Label className="text-[11px] uppercase text-stone-500">Prospect type</Label>
              <div className="flex gap-2 mt-1">
                {PROSPECT_LABELS.map(l => {
                  const active = labels.includes(l.value);
                  return (
                    <button key={l.value} type="button" data-testid={`prospect-label-toggle-${l.value}`} onClick={() => toggleLabel(l.value)}
                      className="px-2.5 py-1 rounded-full text-[12px] font-medium border transition-colors"
                      style={active ? { background: `${l.color}22`, color: l.color, borderColor: `${l.color}66` } : { borderColor: '#e7e5e4', color: '#78716c' }}>
                      {active ? '✓ ' : ''}{l.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[11px] uppercase text-stone-500">Name</Label><Input value={p.name} onChange={(e) => set({ name: e.target.value })} /></div>
              <div><Label className="text-[11px] uppercase text-stone-500">Email</Label><Input value={p.email} onChange={(e) => set({ email: e.target.value })} /></div>
              <div><Label className="text-[11px] uppercase text-stone-500">PR / Agent email</Label><Input value={p.prEmail || ''} onChange={(e) => set({ prEmail: e.target.value })} /></div>
              <div><Label className="text-[11px] uppercase text-stone-500">Cell phone</Label><Input type="tel" data-testid="prospect-phone-input" value={p.phone || ''} onChange={(e) => set({ phone: e.target.value })} placeholder="+1 555 123 4567" /></div>
              <div><Label className="text-[11px] uppercase text-stone-500">Company</Label><Input value={p.company || ''} onChange={(e) => set({ company: e.target.value })} /></div>
              <div><Label className="text-[11px] uppercase text-stone-500">Role</Label><Input value={p.role || ''} onChange={(e) => set({ role: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-[11px] uppercase text-stone-500">Location</Label><Input value={p.location || ''} onChange={(e) => set({ location: e.target.value })} placeholder="e.g. Tbilisi, Georgia · Napa, CA · London, UK" /></div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Source</Label>
                <Select value={p.source} onValueChange={(v) => set({ source: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="text-[11px] uppercase text-stone-500">Source detail</Label><Input value={p.sourceDetail || ''} onChange={(e) => set({ sourceDetail: e.target.value })} placeholder="URL / referrer name / etc" /></div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Status</Label>
                <Select value={p.status} onValueChange={(v) => set({ status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PROSPECT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-[11px] uppercase text-stone-500">Notes</Label><Textarea rows={4} value={p.notes || ''} onChange={(e) => set({ notes: e.target.value })} /></div>
          </div>
        )}

        {tab === 'letter' && (
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between -mt-1">
              <div className="text-[11px] uppercase text-stone-500">Compose letter</div>
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="prospect-merge-fields-info-btn" type="button" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-stone-200 hover:bg-stone-50 text-[10.5px] text-stone-600">
                    <Info className="w-3 h-3" /> Merge fields
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[380px] p-0">
                  <div className="px-3 py-2 border-b border-stone-200 text-[12px] font-medium text-stone-800">What each {'{{tag}}'} resolves to</div>
                  <div className="max-h-[320px] overflow-y-auto divide-y divide-stone-100">
                    {[
                      { tag: '{{firstName}}', src: 'Prospect name (first word)', val: (p.name || '').split(' ')[0] || '—' },
                      { tag: '{{showName}}', src: 'Active show', val: activeShow?.name || '—' },
                      { tag: '{{company}}', src: 'Prospect → Company', val: p.company || '— (empty)' },
                      { tag: '{{bookingLink}}', src: "Show's public /book/ URL (with prospect id)", val: bookingLink },
                      { tag: '{{referrer}}', src: 'Prospect → Source detail (fallback: a mutual friend)', val: p.sourceDetail || 'a mutual friend (fallback)' },
                      { tag: '{{topic}}', src: 'Prospect → Source detail (fallback: your work)', val: p.sourceDetail || 'your work (fallback)' },
                      { tag: '{{showFocus}}', src: 'Wine vs music vertical', val: activeShow?.id === 'wine-talks' ? 'wine' : 'American music & culture' },
                      { tag: '{{hook}}', src: 'AI-generated only — empty in templates', val: '(only filled when using "Generate with AI")' },
                      { tag: '{{specificThought}}', src: 'AI-generated only — empty in templates', val: '(only filled when using "Generate with AI")' }
                    ].map(f => (
                      <div key={f.tag} className="px-3 py-2 text-[11.5px]">
                        <div className="flex items-center justify-between gap-2">
                          <code className="bg-stone-100 px-1.5 py-0.5 rounded text-stone-800">{f.tag}</code>
                          <span className="text-stone-400 text-[10px]">{f.src}</span>
                        </div>
                        <div className="mt-1 text-stone-700 truncate" title={f.val}>{'→ '}{f.val}</div>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select value={draft.templateId} onValueChange={applyTemplate}>
                <SelectTrigger data-testid="prospect-letter-template-select"><SelectValue placeholder="Pick a reach-out template" /></SelectTrigger>
                <SelectContent>
                  {visibleTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.category === 'app_user' ? '🔵 ' : ''}{t.name.replace('[Prospect] ', '').replace('[App User] ', 'App User — ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={generateAi} disabled={drafting}>
                <Wand2 className="w-3.5 h-3.5 mr-1.5" /> {drafting ? 'Generating…' : 'Generate with AI'}
              </Button>
            </div>
            <div><Label className="text-[11px] uppercase text-stone-500">Subject</Label><Input value={draft.subject} onChange={(e) => setDraft(d => ({ ...d, subject: e.target.value }))} /></div>
            <div><Label className="text-[11px] uppercase text-stone-500">Body</Label><Textarea rows={12} value={draft.body} onChange={(e) => setDraft(d => ({ ...d, body: e.target.value }))} className="font-mono text-[12.5px]" /></div>
            {p.prEmail && (
              <label className="flex items-center gap-2 text-[12.5px] text-stone-700">
                <input type="checkbox" checked={draft.includePrEmail} onChange={(e) => setDraft(d => ({ ...d, includePrEmail: e.target.checked }))} className="w-4 h-4 accent-stone-900" />
                Cc PR email ({p.prEmail})
              </label>
            )}
            <div className="flex justify-end gap-2">
              {(p.phone || '').trim() && (
                <Button asChild variant="outline" data-testid="text-prospect-btn" className="border-stone-300">
                  <a href={`sms:${(p.phone || '').replace(/\s+/g, '')}?&body=${encodeURIComponent(`Hi ${(p.name || '').split(' ')[0] || 'there'}, `)}`}>
                    <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Text
                  </a>
                </Button>
              )}
              <Button variant="outline" data-testid="prospect-preview-btn" onClick={() => setPreviewOpen(true)}>
                <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
              </Button>
              <Button onClick={send} disabled={sending} className="text-white" style={{ background: activeShow.color }}>
                <Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? 'Sending…' : 'Send letter'}
              </Button>
            </div>
            <EmailPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} subject={draft.subject} body={draft.body} prospectId={p.id} toEmail={p.email} />
          </div>
        )}

        {tab === 'booking' && (
          <div className="p-5 space-y-3">
            <div className="text-[12.5px] text-stone-600">Personalized booking link. When this prospect books, they’re automatically marked as <strong>booked</strong> and a guest record is created — they disappear from your prospect list and move into Guests.</div>
            <div className="flex items-center gap-2 p-3 rounded border border-stone-200 bg-stone-50">
              <code className="flex-1 text-[12px] text-stone-700 truncate">{bookingLink}</code>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(bookingLink); toast({ title: 'Copied' }); }}><Copy className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={() => window.open(bookingLink, '_blank')}><ExternalLink className="w-3.5 h-3.5" /></Button>
            </div>
            {p.bookedGuestId && (
              <div className="p-3 rounded bg-emerald-50 border border-emerald-200 text-[12.5px] text-emerald-900">✅ Already booked. Guest record id: <code>{p.bookedGuestId}</code></div>
            )}
          </div>
        )}

        {tab === 'emails' && (
          <div className="p-5">
            <EmailHistoryList fetcher={() => Prospects.listEmails(p.id)} dataTestid="prospect-email-history" />
          </div>
        )}

        <div className="px-5 py-4 border-t border-stone-200 mt-4 flex items-center justify-between sticky bottom-0 bg-white">
          <Button variant="ghost" className="text-red-600 hover:bg-red-50" onClick={async () => { await Prospects.remove(p.id); toast({ title: 'Deleted' }); onChanged?.(); onOpenChange(false); }}>
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setTab('letter')}><Mail className="w-3.5 h-3.5 mr-1.5" /> Compose letter</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
